/**
 * Sink Helpers - Utility functions for working with sink objects
 * Handles both legacy nested sinks and new flat storage
 */

import ParentChildSystem from './parentChildSystem';
// shared-lib/sink-types.js is CJS; default-import + destructure so webpack
// can statically track the re-exports below.
import sinkTypesModule from '@shared-lib/sink-types';
const { isSink, getSinkType, SINK_MOUNT_TYPES, buildSinkDimensions } =
  sinkTypesModule;

/**
 * Get all sinks in a room (from room.objects with parents array)
 * @param {Object} room - Room object containing objects
 * @returns {Array} Array of all sink objects
 */
const getAllSinks = (room) => {
  if (!room?.objects || !Array.isArray(room.objects)) {
    return [];
  }

  // Get sinks from room.objects (only new storage location supported)
  return room.objects.filter((obj) => isSink(obj));
};

/**
 * Find the parent countertop of a sink
 * Uses parents array (new storage system)
 * @param {Object} sink - Sink object
 * @param {Object} room - Room containing the objects
 * @returns {Object|null} Parent countertop object or null
 */
const findSinkParentCountertop = (sink, room) => {
  if (!sink || !room?.objects) {
    return null;
  }

  // Check parents array (new structure only)
  if (sink.parents && Array.isArray(sink.parents)) {
    const countertopParent = ParentChildSystem.getParentByType(
      sink,
      'countertop'
    );
    if (countertopParent) {
      return room.objects.find(
        (obj) =>
          obj.id === countertopParent.id &&
          (obj.type === 'countertop' || obj.category?.countertop)
      );
    }
  }

  return null;
};

/**
 * Get sink by ID from either storage location
 * @param {string} sinkId - ID of the sink to find
 * @param {Object} room - Room containing the objects
 * @returns {Object|null} Sink object or null
 */
const getSinkById = (sinkId, room) => {
  if (!sinkId || !room) {
    return null;
  }

  const allSinks = getAllSinks(room);
  return allSinks.find((sink) => sink.id === sinkId) || null;
};

/**
 * Get all sinks on a specific countertop
 * Uses parents array (new storage system)
 * @param {string} countertopId - ID of the countertop
 * @param {Object} room - Room containing the objects
 * @returns {Array} Array of sinks on this countertop
 */
const getSinksOnCountertop = (countertopId, room) => {
  if (!countertopId || !room?.objects) {
    return [];
  }

  // Find sinks with parent relationship (new structure only)
  const sinks = room.objects.filter((obj) => {
    if (!isSink(obj)) return false;

    // Check parents array
    const hasCountertopParent = obj.parents?.some(
      (p) => p.id === countertopId && p.type === 'countertop'
    );

    return hasCountertopParent;
  });

  return sinks;
};

/**
 * Check if a position is over a sink
 * @param {number} x - X coordinate
 * @param {number} z - Z coordinate
 * @param {Object} room - Room containing objects
 * @param {number} tolerance - Distance tolerance in meters
 * @returns {Object|null} Sink at position or null
 */
const findSinkAtPosition = (x, z, room, tolerance = 0.1) => {
  const allSinks = getAllSinks(room);

  for (const sink of allSinks) {
    const sinkCenter = sink.center || {
      x: sink.position?.x || 0,
      z: sink.position?.z || 0,
    };

    const dimensions = sink.dimensions || {
      width: 0.559, // Default 22"
      depth: 0.457, // Default 18"
    };

    const halfWidth = dimensions.width / 2;
    const halfDepth = dimensions.depth / 2;

    // Check if position is within sink bounds
    if (
      x >= sinkCenter.x - halfWidth - tolerance &&
      x <= sinkCenter.x + halfWidth + tolerance &&
      z >= sinkCenter.z - halfDepth - tolerance &&
      z <= sinkCenter.z + halfDepth + tolerance
    ) {
      return sink;
    }
  }

  return null;
};

/**
 * Format a canonical sinkType value for display in the UI
 * @param {string} sinkType - Canonical mount type (e.g. 'undermount', 'vessel')
 * @returns {string|null} Human-readable label, or null for unknown values
 */
const formatSinkType = (sinkType) => {
  const labels = {
    undermount: 'Undermount',
    dropin: 'Drop-In',
    vessel: 'Vessel',
    pedestal: 'Pedestal',
    'wall-mount': 'Wall-Mount',
  };
  return labels[sinkType] ?? null;
};

/**
 * Create a sink object with proper structure
 * @param {string} sinkType - Canonical mount type (e.g. 'undermount', 'vessel')
 * @param {Object} position - Position {x, y, z}
 * @param {string} countertopId - Parent countertop ID
 * @param {Object} dimensions - Optional dimensions
 * @returns {Object} Properly structured sink object
 */
const createSinkObject = (
  sinkType,
  position,
  countertopId,
  dimensions = null
) => {
  const timestamp = Date.now();
  const sinkId = `sink_${sinkType}_${timestamp}`;

  return {
    id: sinkId,
    type: 'sink',
    sinkType,
    category: { sink: true },
    parents: [
      {
        id: countertopId,
        type: 'countertop',
      },
    ],
    center: {
      x: position.x,
      y: position.y,
      z: position.z,
    },
    // Always through the builder: a caller-supplied partial `dimensions`
    // would otherwise write through without x/y/z.
    dimensions: buildSinkDimensions({
      width: dimensions?.width ?? dimensions?.x ?? 0.559, // 22 inches
      depth: dimensions?.depth ?? dimensions?.z ?? 0.457, // 18 inches
      height: dimensions?.height ?? dimensions?.y ?? 0.203, // 8 inches
      cutoutDepth: dimensions?.cutoutDepth,
    }),
    placedAt: new Date().toISOString(),
  };
};

/**
 * Validate sink placement on countertop
 * @param {Object} position - Position to place sink {x, z}
 * @param {Object} countertop - Countertop object
 * @param {Array} existingSinks - Array of existing sinks on countertop
 * @returns {Object} Validation result {valid: boolean, message: string}
 */
const validateSinkPlacement = (position, countertop, existingSinks = []) => {
  // Check max sinks (typically 2 per countertop)
  if (existingSinks.length >= 2) {
    return {
      valid: false,
      message: 'Maximum 2 sinks per countertop',
    };
  }

  // Get countertop dimensions
  const ctDimensions = countertop.dimensions || { x: 2.0, z: 0.635 };
  const ctCenter = countertop.center || { x: 0, z: 0 };

  // Check edge clearance (2 inches = 0.051m)
  const minEdgeClearance = 0.051;
  const sinkDimensions = { width: 0.559, depth: 0.457 }; // Default sink size

  // Measure in the COUNTERTOP'S OWN FRAME. `dimensions` are that object's
  // extents along its own axes, so a world-axis box built from them is only
  // right when rotation.y is 0 — and 210 of 652 countertops in dev (32%) are
  // turned. On those, this rejected placements the caller had just computed
  // correctly (it does branch on rotation) and accepted ones off the slab.
  // Rotating the point instead of the box keeps the test exact and needs no
  // guess about which axis is width. Inverse of the local->world map in
  // `calculateCorners`: world = (lx·cos − lz·sin, lx·sin + lz·cos).
  const ctRotY = countertop.rotation?.y ?? 0;
  const cos = Math.cos(ctRotY);
  const sin = Math.sin(ctRotY);
  const dx = position.x - ctCenter.x;
  const dz = position.z - ctCenter.z;
  const localX = dx * cos + dz * sin;
  const localZ = -dx * sin + dz * cos;

  // The sink is seated square on the countertop, so its own footprint is
  // axis-aligned in this frame.
  const halfSpanX = ctDimensions.x / 2 - minEdgeClearance;
  const halfSpanZ = ctDimensions.z / 2 - minEdgeClearance;

  if (
    Math.abs(localX) + sinkDimensions.width / 2 > halfSpanX ||
    Math.abs(localZ) + sinkDimensions.depth / 2 > halfSpanZ
  ) {
    return {
      valid: false,
      message: 'Sink must be at least 2" from countertop edges',
    };
  }

  // Check spacing from existing sinks (6 inches = 0.152m center-to-center)
  const minSinkSpacing = 0.152;
  for (const existingSink of existingSinks) {
    const existingCenter = existingSink.center || {
      x: existingSink.position?.x || 0,
      z: existingSink.position?.z || 0,
    };

    const dx = position.x - existingCenter.x;
    const dz = position.z - existingCenter.z;
    const distance = Math.sqrt(dx * dx + dz * dz);

    const existingDimensions = existingSink.dimensions || sinkDimensions;
    const minRequired =
      sinkDimensions.width / 2 + existingDimensions.width / 2 + minSinkSpacing;

    if (distance < minRequired) {
      return {
        valid: false,
        message: 'Sinks must be at least 6" apart',
      };
    }
  }

  return {
    valid: true,
    message: 'Valid placement',
  };
};

export {
  isSink,
  getSinkType,
  getAllSinks,
  findSinkParentCountertop,
  getSinkById,
  getSinksOnCountertop,
  findSinkAtPosition,
  formatSinkType,
  createSinkObject,
  validateSinkPlacement,
};
